import type { DelsumInput } from '@klp/prosjekt/services/types';

/** Backend identifies the MVA-kompensasjon row by this exact name (index 1 when present). */
export const MVA_DELSUM_NAVN = 'MVA';
/** Backend identifies the auto-calculated Hovedprosjekt row by this exact name (index 0). */
export const HOVEDPROSJEKT_DELSUM_NAVN = 'Hovedprosjekt';

/** A fresh MVA row. kostnad is omitted — the backend forces it to 25 % of total. */
export const createMvaDelsum = (): DelsumInput => ({
    navn: MVA_DELSUM_NAVN,
    avskrivningstid: 0,
    restverdiIProsent: 0,
});

/** Parses the outer-schema `mvapliktig` value (integer percent as string) to a number, or null. */
export const parseMvapliktig = (value: unknown): number | null => {
    if (value === null || value === undefined) return null;
    const normalized = typeof value === 'string' ? value.trim() : value;
    if (normalized === '') return null;
    const parsed = Number(normalized);
    return Number.isFinite(parsed) ? parsed : null;
};

/**
 * Reconciles the Formik `delsummer` list with `mvapliktig`:
 * - 0-99 and no MVA row and a Hovedprosjekt row exists -> insert MVA at index 1
 * - 100 and MVA row present -> remove it
 * - otherwise -> no change (null)
 *
 * When 0-99 but there is no Hovedprosjekt row at index 0 (should not occur — new
 * schemas are prefilled with it and it is non-removable), we return null rather than
 * inject MVA: a Hovedprosjekt-less list would corrupt the backend's reslice.
 */
export const reconcileMvaDelsum = (
    delsummer: Array<DelsumInput>,
    mvapliktig: number | null,
): Array<DelsumInput> | null => {
    if (mvapliktig === null) return null;

    const hasMva = delsummer.some((d) => d.navn === MVA_DELSUM_NAVN);
    const shouldShow = mvapliktig < 100;

    if (shouldShow && !hasMva) {
        const hasHovedprosjekt =
            delsummer[0]?.navn === HOVEDPROSJEKT_DELSUM_NAVN;
        if (!hasHovedprosjekt) return null;
        return [delsummer[0], createMvaDelsum(), ...delsummer.slice(1)];
    }

    if (!shouldShow && hasMva) {
        return delsummer.filter((d) => d.navn !== MVA_DELSUM_NAVN);
    }

    return null;
};
